/**
 * URL validation utilities for preventing open redirect vulnerabilities.
 */

/**
 * Validates that a path is a safe relative path.
 *
 * A safe relative path:
 * - Starts with a single '/'
 * - Does not start with '//' (protocol-relative URL)
 * - Does not contain a scheme (http://, https://, javascript:, etc.)
 * - Is a valid relative path within the application
 *
 * @param path - The path to validate
 * @returns True if the path is a safe relative path, false otherwise
 */
export function isValidRelativePath(path: string | null | undefined): boolean {
  if (!path || typeof path !== "string") {
    return false;
  }

  // Must start with '/' for relative path
  if (!path.startsWith("/")) {
    return false;
  }

  // Must not start with '//' (protocol-relative URL like //evil.com)
  if (path.startsWith("//")) {
    return false;
  }

  // Must not contain a scheme (http://, https://, javascript:, etc.)
  // Check for common schemes that could be used for redirects
  const schemePattern = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;
  if (schemePattern.test(path)) {
    return false;
  }

  // Additional check: ensure it's not a data URI or other dangerous schemes
  const dangerousSchemes = ["javascript:", "data:", "vbscript:", "file:"];
  const lowerPath = path.toLowerCase();
  if (dangerousSchemes.some((scheme) => lowerPath.startsWith(scheme))) {
    return false;
  }

  return true;
}

/**
 * Validates and sanitizes a returnTo path for safe navigation.
 *
 * @param returnTo - The returnTo path to validate
 * @param fallback - Fallback path if validation fails (default: '/')
 * @returns A safe relative path to navigate to
 */
export function validateAndSanitizeReturnTo(
  returnTo: string | null | undefined,
  fallback: string = "/"
): string {
  if (isValidRelativePath(returnTo) && returnTo) {
    return returnTo;
  }
  return fallback;
}
